import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography, Button, TextField, InputAdornment } from '@mui/material';
import CircularProgress from '@material-ui/core/CircularProgress';

import {
    getDqtCheckoutCatalogService,
    createDqtCheckoutService,
    getDqtCheckoutStatusService,
    getDqtWireOrdersService,
} from '../../../services/billing.service';
import { refreshToken } from '../../../services/auth.service';
import { useAuth } from '../../../context/auth';
import { toast } from '../../../utils/utils';
import { TOAST_TYPES } from '../../../utils/constants';
import { useColorMode } from '../../../context/colorMode/colorMode.context';
import DqtCheckoutDialog, { DqtWireProofDialog } from './DqtCheckoutDialog.comp';

/**
 * DqtPurchasePanel — buy the plan once, then top up DQ Tokens: any USD amount the
 * buyer types, or fills in with a quick-amount button. There are no fixed packs
 * (23 Sep 2026). Each paid purchase is invoiced and listed in Billing history with
 * the workspace's other invoices.
 *
 * Every rule is the server's (/billing/checkout/catalog): which plans are for sale
 * (only before a plan is active, and only once), whether the plan has a top-up, the
 * range of a custom amount, whether this user is the owner who may buy.
 * The panel renders what it is told.
 *
 * `returnSessionId` is the Checkout Session Stripe sent the buyer back with (from
 * `?dqt_checkout=` — redirect-based payment methods). The panel settles it once on
 * mount and then calls `onReturnHandled` so the page can clear the URL.
 *
 * `onPurchased()` lets the page refresh the balances it shows, and
 * `onAvailability({ can_buy_plan, can_top_up })` tells it what this panel is
 * offering — so the DQ Token card can show a Top up button only when there is
 * something to buy.
 *
 * A direct workspace (a school or network buying for itself, 23 Sep 2026) sees the
 * same custom amount in CREDITS — never DQ Tokens: the API converts what it buys into
 * credits for its pool. It can also buy LICENCES by number, at its plan's licence
 * price; they go to its organisation's seat pool.
 *
 * DQ Tokens and credits carry two decimals, rounded to the nearest hundredth, and a
 * custom amount may be any whole number of cents (23 Sep 2026).
 *
 * Inside the program builder (`topUpOnly`) it shows only the DQ Token / credit top-up —
 * no plan purchase, licences or bank-transfer list — and `need` ({ dqt } or
 * { credits }: what the build is short) pre-fills the amount that covers it.
 */

const TEAL = '#0097b2';

const fmtDqt = (n) => `${Number(n || 0).toLocaleString('en-US')} DQT`;
const fmtCredits = (n) => `${Number(n || 0).toLocaleString('en-US')} ${Number(n) === 1 ? 'credit' : 'credits'}`;
const fmtUsd = (n) => `US$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Quick-amount buttons (product owner, 23 Sep 2026). They only fill the amount in —
 * the buyer can type any amount, and the API checks it. Frontend only, by design: no
 * admin setting. A button outside the allowed range is disabled.
 */
const QUICK_AMOUNTS_USD = [100, 500, 1000, 5000];
const QUICK_LICENCES = [200, 500, 1000, 2000];

const fmtLicences = (n) => `${Number(n || 0).toLocaleString('en-US')} ${Number(n) === 1 ? 'licence' : 'licences'}`;

const BLOCK_TEXT = {
    RATE_NOT_SET: 'DQ Tokens are not on sale yet — DQ has not set the token rate.',
    TOPUP_NOT_OFFERED: 'Your plan has no top-ups yet. Contact DQ to add them.',
    NO_ACTIVE_PLAN: 'Top-ups open up once your plan is active.',
    NOT_OWNER: 'Only the workspace owner can buy DQ Tokens.',
    DIRECT_PLAN: 'Your plan does not use DQ Tokens.',
    PRECISION_UNDECIDED: 'Not available at the current token rate.',
    PRICE_NOT_SET: 'Not priced yet.',
};

// A direct workspace (a school or network buying for itself) is shown credits, never
// DQ Tokens (23 Sep 2026): its top-ups are converted into credits for its pool.
const DIRECT_BLOCK_TEXT = {
    ...BLOCK_TEXT,
    RATE_NOT_SET: 'Credits are not on sale yet.',
    NOT_OWNER: 'Only the workspace owner can buy credits.',
    PRICE_NOT_SET: 'Credits cannot be bought on your plan yet. Contact DQ.',
    PRECISION_UNDECIDED: 'Not available right now.',
    LICENCE_PRICE_NOT_SET: 'Licences cannot be bought on your plan yet. Contact DQ.',
};

/** To the nearest hundredth, half up — the API's roundUnits (utils/dq_units.js). */
const round2 = (n) => Math.round(Math.abs(n) * 100 + 1e-7) / 100 * (n < 0 ? -1 : 1);

/**
 * Check a typed USD amount against the catalog's custom offer. Works in cents so
 * 0.1 + 0.2 never matters.
 * @returns {{ ok: true, amount_usd, dqt_amount, credits } | { ok: false, message }}
 */
/**
 * The US$ amount that buys at least `need` ({ dqt } or { credits }), fitted to the
 * catalog's custom offer: rounded up to a whole step, within its range. Null when it
 * cannot be priced.
 */
export const suggestedUsd = (need, custom, usd_per_dqt, direct = null) => {
    if (!need || !custom) return null;
    let usd = null;
    if (need.dqt > 0 && usd_per_dqt > 0) usd = need.dqt * usd_per_dqt;
    else if (need.credits > 0 && direct?.credit_price_usd > 0) usd = need.credits * direct.credit_price_usd;
    if (usd === null) return null;
    const step = Math.max(1, Math.round((custom.step_usd || 0.01) * 100));
    let cents = Math.ceil(Math.round(usd * 1e6) / 1e4);
    cents = Math.ceil(cents / step) * step;
    cents = Math.min(Math.max(cents, Math.round(custom.min_usd * 100)), Math.round(custom.max_usd * 100));
    return cents / 100;
};

const checkCustomAmount = (text, custom, usd_per_dqt, direct = null) => {
    const value = String(text || '').trim();
    if (!value) return { ok: false, message: '' };
    if (!/^\d+(\.\d{1,2})?$/.test(value)) return { ok: false, message: 'Enter a US dollar amount, with at most 2 decimal places.' };
    const cents = Math.round(Number(value) * 100);
    const min = Math.round(custom.min_usd * 100);
    const max = Math.round(custom.max_usd * 100);
    if (cents < min || cents > max) return { ok: false, message: `Enter between ${fmtUsd(custom.min_usd)} and ${fmtUsd(custom.max_usd)}.` };
    const step = Math.round(custom.step_usd * 100);
    if (step > 1 && cents % step !== 0) {
        return { ok: false, message: `Use steps of ${fmtUsd(custom.step_usd)}.` };
    }
    const dqt_amount = round2(cents / 100 / usd_per_dqt);
    // Credits include any DQ Tokens the workspace still holds (never shown as DQT).
    const credits = direct ? round2((dqt_amount + (direct.carried_dqt || 0)) / direct.credit_price_dqt) : null;
    return { ok: true, amount_usd: cents / 100, dqt_amount, credits };
};

// ─── Building blocks — the Billing tab's own card language ───────────────────
// Same surface, border, 20px corners, 4px accent stripe, uppercase label and big
// figure as the Credit Balance and DQ Token Balance cards beside this panel, so the
// top-ups read as part of that page. Colours come from the --brand-* tokens, which
// the colour-mode provider flips for dark mode; the few that cannot (pill borders,
// disabled states, status chips) ask useColorMode().

// Credits are orange on this page (the Credit Balance card); DQ Tokens and
// licences are teal (the DQ Token Balance card).
const ORANGE = '#FF6900';
const HOVER = { [TEAL]: '#007f96', [ORANGE]: '#e55e00' };
const GLOW = { [TEAL]: 'rgba(0,151,178,0.25)', [ORANGE]: 'rgba(255,105,0,0.25)' };

const fmtNumber = (n) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 });
const fmtUsdShort = (n) => `$${Number(n).toLocaleString('en-US')}`;

const PanelCard = ({ accent = TEAL, children }) => (
    <Box sx={{
        flex: '1 1 300px', minWidth: 0, maxWidth: 440,
        background: 'var(--brand-surface, #fff)', border: '1px solid var(--brand-border, #E5E7EB)',
        borderRadius: '20px', p: '28px 28px 24px',
        display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 2.5,
        position: 'relative', overflow: 'hidden',
    }}>
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: accent, borderRadius: '20px 20px 0 0' }}/>
        {children}
    </Box>
);

const CardLabel = ({ children }) => (
    <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#9CA3AF', letterSpacing: '1.5px', textTransform: 'uppercase', mb: 0.75 }}>
        {children}
    </Typography>
);

const CardNote = ({ children, sx }) => (
    <Typography sx={{ fontSize: '0.825rem', color: 'var(--brand-text-muted, #94a3b8)', ...sx }}>{children}</Typography>
);

/** The big figure and its unit, as on the balance cards. */
const Figure = ({ value, unit, accent }) => (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, mt: 2.5, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: 40, fontWeight: 900, color: accent, lineHeight: 1 }}>{value}</Typography>
        {unit ? (
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: accent, textTransform: 'uppercase', mb: 0.4 }}>{unit}</Typography>
        ) : null}
    </Box>
);

/** The card's action — the balance cards' button, with a disabled look that reads in both modes. */
const ActionButton = ({ accent = TEAL, busy, disabled, onClick, children }) => {
    const { isDark } = useColorMode();
    return (
        <Button
            variant="contained" fullWidth disabled={disabled || busy} onClick={onClick}
            sx={{
                backgroundColor: accent, color: '#fff', borderRadius: '10px', textTransform: 'none',
                fontWeight: 700, py: 1.1, fontSize: '0.875rem', boxShadow: 'none',
                '&:hover': { backgroundColor: HOVER[accent] || accent, boxShadow: `0 4px 12px ${GLOW[accent] || 'transparent'}` },
                '&.Mui-disabled': {
                    backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : '#E5E7EB',
                    color: isDark ? 'rgba(255,255,255,0.38)' : '#9CA3AF',
                },
            }}
        >
            {busy ? <CircularProgress size={18} style={{ color: '#fff' }}/> : children}
        </Button>
    );
};

/** The amount / licence field: rounded like the page's inputs, accent on focus. */
const fieldSx = (accent) => ({
    mt: 1.75,
    '& .MuiOutlinedInput-root': { borderRadius: '10px' },
    '& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': { borderColor: accent },
    '& .MuiInputLabel-root.Mui-focused:not(.Mui-error)': { color: accent },
});

/** A row of pill buttons that fill the field in (the screenshots of 23 Sep 2026). */
const QuickFill = ({ values, format, isAllowed, selected, onPick, disabled, accent = TEAL }) => {
    const { isDark } = useColorMode();
    return (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
            {values.map((v) => {
                const on = Number(selected) === v;
                return (
                    <Button
                        key={v} size="small" variant="outlined" aria-pressed={on}
                        disabled={disabled || !isAllowed(v)} onClick={() => onPick(v)}
                        sx={{
                            borderRadius: '999px', textTransform: 'none', fontWeight: 700, fontSize: '0.85rem',
                            px: 2, py: 0.5, minWidth: 0, boxShadow: 'none',
                            borderColor: on ? accent : (isDark ? 'rgba(255,255,255,0.24)' : '#D1D5DB'),
                            color: on ? '#fff' : 'var(--brand-text, #111827)',
                            backgroundColor: on ? accent : 'transparent',
                            '&:hover': {
                                borderColor: accent,
                                backgroundColor: on ? (HOVER[accent] || accent) : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)'),
                            },
                            '&.Mui-disabled': {
                                borderColor: isDark ? 'rgba(255,255,255,0.1)' : '#E5E7EB',
                                color: isDark ? 'rgba(255,255,255,0.3)' : '#C0C4CC',
                            },
                        }}
                    >
                        {format(v)}
                    </Button>
                );
            })}
        </Box>
    );
};

/** A reason the buyer cannot buy something — amber, readable in both modes. */
const BlockedNote = ({ children }) => {
    const { isDark } = useColorMode();
    return (
        <Box sx={{
            mt: 2, p: '10px 14px', borderRadius: '10px', fontSize: '0.825rem', lineHeight: 1.5,
            background: isDark ? 'rgba(245,158,11,0.12)' : '#FFFBEB',
            border: `1px solid ${isDark ? 'rgba(245,158,11,0.35)' : '#FDE68A'}`,
            color: isDark ? '#FCD34D' : '#92400E',
        }}>
            {children}
        </Box>
    );
};

/** A section of the panel, headed like the page's other sections (Feature Access, Licenses). */
const Section = ({ title, text, children }) => (
    <Box sx={{ mb: 4 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 18, mb: 0.5, color: 'var(--brand-text, #111827)' }}>{title}</Typography>
        {text ? <Typography sx={{ fontSize: 14, color: 'var(--brand-text-muted, #6B7280)', mb: 2.5 }}>{text}</Typography> : null}
        {children}
    </Box>
);

const CustomAmountCard = ({ custom, usd_per_dqt, direct = null, need = null, busy, onBuy }) => {
    const [text, setText] = useState(() => {
        const covering = suggestedUsd(need, custom, usd_per_dqt, direct);
        if (covering !== null) return covering.toFixed(2);
        return custom.preset_usd ? String(custom.preset_usd) : '';
    });
    const check = checkCustomAmount(text, custom, usd_per_dqt, direct);
    const accent = direct ? ORANGE : TEAL;
    return (
        <PanelCard accent={accent}>
            <Box>
                <CardLabel>{direct ? 'Buy credits' : 'Buy DQ Tokens'}</CardLabel>
                <CardNote>
                    {direct ? `${fmtUsd(direct.credit_price_usd)} per credit` : `${fmtUsd(usd_per_dqt)} per DQ Token`}
                </CardNote>
                <QuickFill
                    values={QUICK_AMOUNTS_USD} format={fmtUsdShort} selected={text} disabled={busy} accent={accent}
                    isAllowed={(v) => v >= custom.min_usd && v <= custom.max_usd}
                    onPick={(v) => setText(String(v))}
                />
                <TextField
                    fullWidth size="small" sx={fieldSx(accent)}
                    label="Top-up amount"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && check.ok && !busy) onBuy(check.amount_usd); }}
                    placeholder="0.00"
                    inputProps={{ inputMode: 'decimal' }}
                    InputProps={{ startAdornment: <InputAdornment position="start">US$</InputAdornment> }}
                    error={!!text && !check.ok}
                    helperText={text && !check.ok ? check.message : `Enter an amount from ${fmtUsd(custom.min_usd)} to ${fmtUsd(custom.max_usd)}`}
                />
                <Figure
                    accent={accent}
                    value={check.ok ? fmtNumber(direct ? check.credits : check.dqt_amount) : '—'}
                    unit={direct ? (check.ok && check.credits === 1 ? 'credit' : 'credits') : 'DQT'}
                />
                <CardNote sx={{ mt: 0.75 }}>{check.ok ? `for ${fmtUsd(check.amount_usd)} · one-time` : 'one-time'}</CardNote>
            </Box>
            <ActionButton accent={accent} busy={busy} disabled={!check.ok} onClick={() => onBuy(check.amount_usd)}>
                {direct ? 'Buy credits' : 'Buy DQ Tokens'}
            </ActionButton>
        </PanelCard>
    );
};

/**
 * A direct workspace's licence top-up: a whole number of licences, typed or filled in
 * with a quick button; the price is the licences at the plan's licence price.
 */
const LicenceCard = ({ offer, blockText, busy, onBuy }) => {
    const [text, setText] = useState('');
    const value = String(text || '').trim();
    const count = /^\d+$/.test(value) ? Number(value) : null;
    let message = '';
    if (value && count === null) message = 'Enter a whole number of licences.';
    else if (count !== null && (count < offer.min_licences || count > offer.max_licences)) {
        message = `Choose between ${offer.min_licences.toLocaleString('en-US')} and ${offer.max_licences.toLocaleString('en-US')} licences.`;
    }
    const ok = count !== null && count > 0 && !message;
    // Cents, so 0.1 + 0.2 never matters — the API charges round(licences × price) cents.
    const priceCents = ok ? Math.round(count * offer.licence_price_usd * 100) : null;
    return (
        <PanelCard accent={TEAL}>
            <Box>
                <CardLabel>Buy licences</CardLabel>
                {offer.available ? (
                    <>
                        <CardNote>{fmtUsd(offer.licence_price_usd)} per licence · one licence per learner</CardNote>
                        <QuickFill
                            values={QUICK_LICENCES} format={(v) => v.toLocaleString('en-US')} selected={text} disabled={busy} accent={TEAL}
                            isAllowed={(v) => v >= offer.min_licences && v <= offer.max_licences}
                            onPick={(v) => setText(String(v))}
                        />
                        <TextField
                            fullWidth size="small" sx={fieldSx(TEAL)}
                            label="Number of licences"
                            value={text}
                            onChange={(e) => setText(e.target.value.replace(/[^\d]/g, '').slice(0, 7))}
                            onKeyDown={(e) => { if (e.key === 'Enter' && ok && !busy) onBuy(count); }}
                            placeholder="0"
                            inputProps={{ inputMode: 'numeric' }}
                            error={!!message}
                            helperText={message || `Enter from ${offer.min_licences.toLocaleString('en-US')} to ${offer.max_licences.toLocaleString('en-US')} licences`}
                        />
                        <Figure accent={TEAL} value={ok ? fmtUsd(priceCents / 100) : 'US$—'}/>
                        <CardNote sx={{ mt: 0.75 }}>{ok ? `for ${fmtLicences(count)} · one-time` : 'one-time'}</CardNote>
                    </>
                ) : (
                    <BlockedNote>{blockText[offer.unavailable_reason] || 'Not available right now.'}</BlockedNote>
                )}
            </Box>
            {offer.available && (
                <ActionButton accent={TEAL} busy={busy} disabled={!ok} onClick={() => onBuy(count)}>
                    Buy licences
                </ActionButton>
            )}
        </PanelCard>
    );
};

const OfferCard = ({ title, subtitle, amount_usd, dqt_amount, receive = null, blockText = BLOCK_TEXT, available, unavailable_reason, cta, busy, onBuy }) => (
    <PanelCard accent={TEAL}>
        <Box>
            <CardLabel>Plan</CardLabel>
            <Typography sx={{ fontWeight: 800, fontSize: 20, color: 'var(--brand-text, #111827)', lineHeight: 1.2 }}>{title}</Typography>
            {subtitle ? <CardNote sx={{ mt: 0.5 }}>{subtitle}</CardNote> : null}
            {available ? (
                <>
                    {receive
                        ? <Figure accent={TEAL} value={receive}/>
                        : <Figure accent={TEAL} value={fmtNumber(dqt_amount)} unit="DQT"/>}
                    <CardNote sx={{ mt: 0.75 }}>{fmtUsd(amount_usd)} · one-time</CardNote>
                </>
            ) : (
                <BlockedNote>{blockText[unavailable_reason] || 'Not available right now.'}</BlockedNote>
            )}
        </Box>
        <ActionButton accent={TEAL} busy={busy} disabled={!available} onClick={onBuy}>{cta}</ActionButton>
    </PanelCard>
);

const WIRE_STATUS = {
    awaiting_proof: { label: 'Waiting for your proof', light: ['#FEF3C7', '#92400E'], dark: ['rgba(245,158,11,0.16)', '#FCD34D'] },
    under_review:   { label: 'Under review',           light: ['#DBEAFE', '#1E40AF'], dark: ['rgba(59,130,246,0.18)', '#93C5FD'] },
    rejected:       { label: 'Rejected',               light: ['#FEE2E2', '#991B1B'], dark: ['rgba(239,68,68,0.18)', '#FCA5A5'] },
};

/** Bank transfers the buyer still has to act on or wait for, and recent rejections. */
const WireTransfers = ({ orders, direct, onUploadProof }) => {
    const { isDark } = useColorMode();
    return (
        <Section
            title="Bank transfers"
            text={`Your ${direct ? 'credits and licences are' : 'DQ Tokens are'} added once DQ approves the transfer. Approved transfers appear in Billing history.`}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {orders.map((o) => {
                    const st = WIRE_STATUS[o.wire?.status] || WIRE_STATUS.under_review;
                    const [chipBg, chipFg] = isDark ? st.dark : st.light;
                    return (
                        <Box key={o.order_id} sx={{
                            display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', p: '14px 18px', borderRadius: '14px',
                            border: '1px solid var(--brand-border, #E5E7EB)', background: 'var(--brand-surface, #fff)',
                        }}>
                            <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--brand-text, #111827)' }}>
                                    {o.label} · {fmtUsd(o.amount_usd)}{o.dqt_amount ? ` · ${fmtDqt(o.dqt_amount)}` : ''}{o.licences ? ` · ${fmtLicences(o.licences)}` : ''}
                                </Typography>
                                <CardNote sx={{ fontSize: '0.78rem' }}>
                                    Invoice {o.invoice_no || '—'}
                                    {o.wire?.status === 'rejected' && o.wire?.review_note ? ` — ${o.wire.review_note}` : ''}
                                </CardNote>
                            </Box>
                            <Box sx={{ px: 1.25, py: 0.4, borderRadius: '999px', fontSize: 12, fontWeight: 700, background: chipBg, color: chipFg }}>
                                {st.label}
                            </Box>
                            {o.wire?.status === 'awaiting_proof' && (
                                <Button size="small" variant="outlined" onClick={() => onUploadProof(o)}
                                    sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px', borderColor: TEAL, color: TEAL,
                                        '&:hover': { borderColor: HOVER[TEAL], backgroundColor: 'rgba(0,151,178,0.08)' } }}>
                                    Upload proof
                                </Button>
                            )}
                        </Box>
                    );
                })}
            </Box>
        </Section>
    );
};

// A rejection stays listed for this long, so the buyer sees why.
const REJECTION_VISIBLE_MS = 30 * 24 * 60 * 60 * 1000;

const DqtPurchasePanel = React.forwardRef(({ publishableKey, returnSessionId, onReturnHandled, onPurchased, onAvailability, topUpOnly = false, need = null }, ref) => {
    const { authUser, setAuthToken, setAuthUser } = useAuth();
    const { isDark } = useColorMode();
    const [catalog, setCatalog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busyKey, setBusyKey] = useState(null);
    const [checkout, setCheckout] = useState(null);   // { client_secret, item, payload, wallets_enabled }
    const [wireOrders, setWireOrders] = useState([]);
    const [proofOrder, setProofOrder] = useState(null);
    const [settling, setSettling] = useState(false);
    const returnHandled = useRef(false);

    const load = useCallback(async () => {
        try {
            const [c, w] = await Promise.all([
                getDqtCheckoutCatalogService(),
                getDqtWireOrdersService({ limit: 20 }).catch(() => null),
            ]);
            setCatalog(c?.data || null);
            const now = Date.now();
            setWireOrders((w?.data?.orders || []).filter((o) => ['awaiting_proof', 'under_review'].includes(o.wire?.status)
                || (o.wire?.status === 'rejected' && now - new Date(o.wire.reviewed_at || o.created_at).getTime() < REJECTION_VISIBLE_MS)));
        } catch (_) {
            // invokeApi has already shown the error.
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    /** Refresh the session so a newly active plan (and its access level) reaches the UI. */
    const refreshSession = async () => {
        try {
            const res = await refreshToken();
            if (res?.data?.auth_token) setAuthToken(res.data.auth_token);
            if (res?.data?.user) setAuthUser(res.data.user);
        } catch (_) {}
    };

    /** Ask the server where a checkout ended up; it fulfils a paid one. */
    const settle = useCallback(async (sessionId) => {
        if (!sessionId) return;
        setSettling(true);
        try {
            const res = await getDqtCheckoutStatusService(sessionId);
            const order = res?.data?.order;
            if (order?.status === 'paid') {
                const added = order.licences
                    ? `${fmtLicences(order.licences)} added to your organisation.`
                    : order.direct
                        ? (order.credits ? `${fmtCredits(order.credits)} added to your balance.` : 'Your credits are being added.')
                        : `${fmtDqt(order.dqt_amount)} added to your balance.`;
                toast(TOAST_TYPES.SUCCESS, 'Payment received', `${added}${order.invoice_no ? ` Invoice ${order.invoice_no} is in Billing history.` : ''}`);
                if (order.kind === 'plan' && order.plan_activated) {
                    toast(TOAST_TYPES.SUCCESS, 'Plan active', `${order.label} is now active on your workspace.`);
                    await refreshSession();
                }
                if (onPurchased) onPurchased(order);
            } else if (order?.status === 'pending') {
                toast(TOAST_TYPES.INFO, 'Payment processing', `Your payment is still being confirmed. Your ${order.licences ? 'licences' : order.direct ? 'credits' : 'DQ Tokens'} will be added as soon as it settles.`);
            } else if (order) {
                toast(TOAST_TYPES.WARN, 'Payment not completed', 'The payment did not go through. You have not been charged.');
            }
        } catch (_) {
            // invokeApi has already shown the error.
        } finally {
            setSettling(false);
            load();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [load, onPurchased]);

    // Stripe sent the buyer back (redirect-based payment method).
    useEffect(() => {
        if (!returnSessionId || returnHandled.current) return;
        returnHandled.current = true;
        settle(returnSessionId).finally(() => { if (onReturnHandled) onReturnHandled(); });
    }, [returnSessionId, settle, onReturnHandled]);

    const startCheckout = async (payload, item, key) => {
        if (busyKey) return;
        setBusyKey(key);
        try {
            const res = await createDqtCheckoutService(payload);
            if (res?.data?.client_secret) {
                setCheckout({
                    client_secret: res.data.client_secret,
                    item: { ...item, amount_usd: res.data.amount_usd, dqt_amount: res.data.dqt_amount, credits: res.data.credits ?? null, licences: res.data.licences ?? null },
                    payload,
                    wallets_enabled: !!res.data.wallets_enabled,
                    saved_cards: res.data.saved_cards || [],
                });
            }
        } catch (_) {
            // invokeApi has already toasted the server's reason.
        } finally {
            setBusyKey(null);
        }
    };

    const { plan_purchase: planPurchase, topup } = catalog || {};
    const showPlans = !topUpOnly && !!(planPurchase?.eligible && planPurchase.plans?.length > 0);
    const custom = topup?.eligible && topup.custom?.available ? topup.custom : null;
    const isDirect = catalog?.channel === 'direct';
    const direct = isDirect ? (topup?.direct || null) : null;
    // A direct workspace also buys licences by number (23 Sep 2026).
    const licenceOffer = !topUpOnly && isDirect && topup?.eligible && topup.licences ? topup.licences : null;
    const showTopups = !!custom || !!licenceOffer?.available;
    const blockText = isDirect ? DIRECT_BLOCK_TEXT : BLOCK_TEXT;

    // Tell the page what is on offer (it decides whether to show a Top up shortcut).
    useEffect(() => {
        if (loading || !catalog || !onAvailability) return;
        onAvailability({ can_buy_plan: showPlans, can_top_up: showTopups });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, catalog, showPlans, showTopups]);

    if (loading || !catalog) return null;
    // Say why rather than render nothing: an owner who cannot see top-ups should be
    // told whether the plan is inactive, unpriced, or simply has none configured.
    const topupNote = showTopups ? null
        : (blockText[topup?.blocked_reason] || (topup?.eligible ? 'Top-ups are not available right now. Contact DQ.' : null));

    // In the program builder the panel is the whole dialog: never leave it blank.
    if (topUpOnly && !showTopups && !topupNote) return <BlockedNote>Top-ups are not available right now. Contact DQ.</BlockedNote>;
    if (!showPlans && !showTopups && !topupNote && wireOrders.length === 0) return null;

    const handleWireSubmitted = () => {
        toast(TOAST_TYPES.SUCCESS, 'Proof received', `DQ will review your transfer. Your ${isDirect ? 'credits are' : 'DQ Tokens are'} added once it is approved.`);
        load();
    };

    return (
        <Box ref={ref} sx={{ mt: 4, scrollMarginTop: '80px' }}>
            {!topUpOnly && wireOrders.length > 0 && <WireTransfers orders={wireOrders} direct={isDirect} onUploadProof={setProofOrder}/>}

            {/*{showPlans && (*/}
            {/*    <Section title="Activate your plan" text="A plan is bought once and comes with DQ Tokens. You can top up afterwards.">*/}
            {/*        <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', alignItems: 'stretch' }}>*/}
            {/*            {planPurchase.plans.map((p) => (*/}
            {/*                <OfferCard*/}
            {/*                    key={p.plan_key} title={p.label} subtitle={p.subtitle}*/}
            {/*                    amount_usd={p.amount_usd} dqt_amount={p.dqt_amount}*/}
            {/*                    available={p.available} unavailable_reason={p.unavailable_reason}*/}
            {/*                    cta="Buy plan" busy={busyKey === `plan:${p.plan_key}`}*/}
            {/*                    onBuy={() => startCheckout({ kind: 'plan', plan_key: p.plan_key }, { label: `${p.label} plan` }, `plan:${p.plan_key}`)}*/}
            {/*                />*/}
            {/*            ))}*/}
            {/*        </Box>*/}
            {/*    </Section>*/}
            {/*)}*/}

            {(showTopups || topupNote) && (
                <Section
                    title={isDirect ? 'Buy credits and licences' : 'Buy DQ Tokens'}
                    text={topupNote ? null : (isDirect
                        ? 'Choose an amount of credits, or a number of licences. Pay in your own currency — what you get is the same.'
                        : 'Choose an amount, or type your own. Pay in your own currency — the DQ Tokens are the same.')}
                >
                    {topupNote ? (
                        <BlockedNote>{topupNote}</BlockedNote>
                    ) : (
                        <>
                            <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', alignItems: 'stretch' }}>
                                {custom && (
                                    <CustomAmountCard
                                        custom={custom} usd_per_dqt={catalog.usd_per_dqt} direct={direct} need={need} busy={busyKey === 'custom'}
                                        onBuy={(amount_usd) => startCheckout({ kind: 'topup', amount_usd }, { label: isDirect ? 'Credits top-up' : 'DQ Tokens top-up' }, 'custom')}
                                    />
                                )}
                                {licenceOffer && (
                                    <LicenceCard
                                        offer={licenceOffer} blockText={blockText} busy={busyKey === 'licences'}
                                        onBuy={(licences) => startCheckout({ kind: 'topup', licences }, { label: 'Licences top-up', unit: 'licence' }, 'licences')}
                                    />
                                )}
                            </Box>
                        </>
                    )}
                </Section>
            )}

            <DqtCheckoutDialog
                open={!!checkout}
                publishableKey={publishableKey}
                clientSecret={checkout?.client_secret}
                item={checkout?.item}
                payload={checkout?.payload}
                walletsEnabled={checkout?.wallets_enabled}
                savedCards={checkout?.saved_cards}
                paymentOptions={catalog.payment_options}
                receiveLabel={checkout?.item?.unit === 'licence' ? 'licences' : isDirect ? 'credits' : 'DQ Tokens'}
                receiveText={checkout?.item?.licences
                    ? fmtLicences(checkout.item.licences)
                    : (isDirect && checkout?.item?.credits !== null && checkout?.item?.credits !== undefined ? fmtCredits(checkout.item.credits) : null)}
                fallbackEmail={authUser?.email}
                onClose={() => { setCheckout(null); load(); }}
                onPaid={(sessionId) => { setCheckout(null); settle(sessionId); }}
                onWireSubmitted={handleWireSubmitted}
            />

            <DqtWireProofDialog
                open={!!proofOrder}
                order={proofOrder}
                paymentOptions={catalog.payment_options}
                onClose={() => setProofOrder(null)}
                onWireSubmitted={() => { handleWireSubmitted(); }}
            />

            {settling && (
                <Box sx={{ position: 'fixed', inset: 0, background: isDark ? 'rgba(18,18,18,0.72)' : 'rgba(255,255,255,0.6)', zIndex: 1400,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1.5 }}>
                    <CircularProgress style={{ color: TEAL }}/>
                    <Typography sx={{ fontWeight: 600, color: 'var(--brand-text, #111827)' }}>Confirming your payment…</Typography>
                </Box>
            )}
        </Box>
    );
});

DqtPurchasePanel.displayName = 'DqtPurchasePanel';

export default DqtPurchasePanel;
